import {useContext, useState, useEffect, useMemo} from "react";
import {Modal} from "../../../Smart/Modal/Modal";
import {FormInput} from "../../../Form/Input/FormInput";
import {Button} from "../../../Form/Button/Button";
import {FileUploadBox} from "../../../Smart/FileUploadBox/FileUploadBox";
import {InputCharCount} from "../../../../Utils/InputCharCount/InputCharCount";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {buttonColors} from "../../../Pickers/InputTheme";
import useSignDocTypes from "../../../../Hooks/useSignDocTypes";
import {SignDocTypes} from "../../../SignDoc/SignDocLib";
import {extractFileTextAPI} from "../../../../Services/api";
import {SelectBox} from "../../../Pickers/SelectBox/SelectBox";
import './AIContextGenerator.scss';

const MAX_CHARACTER_COUNT = 25000;

/**
 * AIContextGenerator Component
 *
 * A modal for collecting AI context generation data (subject, instructions, file upload).
 * Can be used standalone or embedded in other components like wizards.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open (only used in standalone mode)
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {boolean} [props.embeddedMode=false] - When true, returns content without Modal wrapper
 * @param {string} [props.signDocType] - Type of SignDoc (EXAM, SURVEY, etc.) - shows question quantity selector for EXAM/SURVEY
 * @param {Function} [props.onDataChange] - Callback when context data changes (receives {subject, promptInstructions, extractedFileText, uploadedFiles, questionQuantity})
 * @param {Function} [props.onValidationChange] - Callback when validation state changes (receives {isValid, errors})
 * @param {Function} [props.onSave] - Callback when save button is clicked
 * @param {Function} [props.validateForm] - Callback to validate the form
 * @returns {JSX.Element} The rendered component
 */
export const AIContextGenerator = ({
    open = true,
    onClose,
    embeddedMode = false,
    signDocType,
    onDataChange,
    onValidationChange,
    onSave,
    validateForm = () => true
}) => {
    const {i18n} = useContext(I18nReactContext);

    // Get signDocType config functions through the hook
    const {getFileUploadConfig, getAIGenerationTitle} = useSignDocTypes();

    // Get file upload configuration for AI course generation (WEEZBEE_COURSE)
    // This configuration supports PDF and TXT files as input materials for AI generation
    const fileUploadConfig = useMemo(() => {
        return getFileUploadConfig({ type: SignDocTypes.WEEZBEE_COURSE });
    }, [getFileUploadConfig]);

    // Form state
    const [subject, setSubject] = useState("");
    const [promptInstructions, setPromptInstructions] = useState("");
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [extractedFileText, setExtractedFileText] = useState("");
    const [isExtractingFile, setIsExtractingFile] = useState(false);
    const [questionQuantity, setQuestionQuantity] = useState(10);

    // Check if question quantity selector should be shown (only for EXAM and SURVEY types)
    const showQuestionQuantitySelector = signDocType === SignDocTypes.EXAM || signDocType === SignDocTypes.SURVEY;

    // Validation state
    const [subjectError, setSubjectError] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Calculate total character count
    const totalCharacterCount = subject.length + promptInstructions.length + extractedFileText.length;
    const isCharacterLimitExceeded = totalCharacterCount > MAX_CHARACTER_COUNT;

    // Reset state when modal closes
    useEffect(() => {
        if (!open && !embeddedMode) {
            setSubject("");
            setPromptInstructions("");
            setUploadedFiles([]);
            setExtractedFileText("");
            setIsExtractingFile(false);
            setSubjectError(false);
            setShowValidationErrors(false);
        }
    }, [open, embeddedMode]);

    // Notify parent of data changes
    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                subject,
                promptInstructions,
                extractedFileText,
                uploadedFiles,
                questionQuantity
            });
        }
    }, [subject, promptInstructions, extractedFileText, uploadedFiles, questionQuantity, onDataChange]);

    // Notify parent of validation changes
    useEffect(() => {
        if (onValidationChange) {
            const isValid = subject.trim().length > 0 && !isCharacterLimitExceeded && !isExtractingFile;
            const errors = {
                subject: !subject.trim() ? i18n.translate('weezbeeCourse.aiWizard.subjectRequired') : null,
                characterLimit: isCharacterLimitExceeded ? i18n.translate('weezbeeCourse.aiWizard.characterLimitExceeded') : null
            };
            onValidationChange({ isValid, errors });
        }
    }, [subject, isCharacterLimitExceeded, isExtractingFile, onValidationChange, i18n]);

    // Memoize form validation result to avoid calling validateForm on every render
    const isFormValid = useMemo(() => {
        return validateForm();
    }, [validateForm]);

    /**
     * Handles changes to the subject input field
     * @param {Object} e - The event object
     */
    const handleSubjectChange = (e) => {
        const value = e.target.value || "";
        setSubject(value);
        if (value.trim().length > 0) {
            setSubjectError(false);
        }
    };

    /**
     * Handles changes to the prompt instructions field
     * @param {Object} e - The event object
     */
    const handlePromptChange = (e) => {
        const value = e.target.value || "";
        setPromptInstructions(value);
    };

    /**
     * Handles changes to the question quantity selector
     * @param {Object} e - The event object
     */
    const handleQuestionQuantityChange = (e) => {
        setQuestionQuantity(e.target.value);
    };

    /**
     * Handles file selection from FileUploadBox
     * @param {Object} params - Parameters object
     * @param {Array} params.files - Array of selected files
     */
    const handleFileUpload = async ({files}) => {
        if (!files || files.length === 0) {
            setUploadedFiles([]);
            setExtractedFileText("");
            return;
        }

        const file = files[0]; // Only handle first file
        setUploadedFiles(files);
        setIsExtractingFile(true);

        try {
            const result = await extractFileTextAPI({ file });
            if (result && result.text) {
                setExtractedFileText(result.text);
            } else {
                 setExtractedFileText("");
            }
        } catch (error) {
            console.error('Error extracting file text:', error);
            // Display specific error message if available, otherwise generic
            const errorMsg = error?.data?.error ? error.data.error : i18n.translate('weezbeeCourse.aiWizard.extractError');

            // If the error message mentions scanned document, display it clearly to the user
            if (error?.data?.errorMessage && (error.data.errorMessage.includes('scanned') || error.data.errorMessage.includes('Textract'))) {
                 setExtractedFileText(`[Error: ${error.data.errorMessage}]`);
            } else {
                 setExtractedFileText(`[Error: ${errorMsg || 'Failed to extract text'}]`);
            }
        } finally {
            setIsExtractingFile(false);
        }
    };

    const content = (
        <div className="AIContextGenerator">
            {/* Subject Field (Required) */}
            <div className="AIContextGenerator__field">
                <FormInput
                    id="context-subject"
                    label={i18n.translate('weezbeeCourse.aiWizard.subjectLabel')}
                    placeholder={i18n.translate('weezbeeCourse.aiWizard.subjectPlaceholder')}
                    value={subject}
                    onChange={handleSubjectChange}
                    required={true}
                    error={showValidationErrors && subjectError ? i18n.translate('weezbeeCourse.aiWizard.subjectRequired') : false}
                    showErrorWhenEmpty={showValidationErrors}
                    dataTestId="context-subject-input"
                />
            </div>

            {/* Prompt Instructions Field (Optional) */}
            <div className="AIContextGenerator__field">
                <FormInput
                    id="context-prompt"
                    label={i18n.translate('weezbeeCourse.aiWizard.promptLabel')}
                    placeholder={i18n.translate('weezbeeCourse.aiWizard.promptPlaceholder')}
                    value={promptInstructions}
                    onChange={handlePromptChange}
                    multiline={true}
                    minRows={4}
                    maxRows={12}
                    dataTestId="context-prompt-input"
                />
            </div>

            {/* Question Quantity Selector (Only for EXAM/SURVEY) */}
            {showQuestionQuantitySelector && (
                <div className="AIContextGenerator__field">
                    <SelectBox
                        label={i18n.translate('questionnaireQuestions.aiModal.questionQuantityLabel')}
                        value={questionQuantity}
                        onChange={handleQuestionQuantityChange}
                        options={[
                            {value: 5, text: '5'},
                            {value: 10, text: '10'},
                            {value: 15, text: '15'}
                        ]}
                        dataTestId="question-quantity-selector"
                    />
                </div>
            )}

            {/* File Upload */}
            <div className="AIContextGenerator__field AIContextGenerator__file-upload">
                <FileUploadBox
                    uploadFiles={handleFileUpload}
                    maxSize={fileUploadConfig.maxSize}
                    accept={fileUploadConfig.accept}
                    supportedFileTypesForNotifications={fileUploadConfig.supportedFileTypesForNotifications}
                >
                    <div>{i18n.translate('weezbeeCourse.aiWizard.uploadFileButton')}</div>
                    {isExtractingFile && (
                        <div className="AIContextGenerator__file-loading">
                            {i18n.translate('weezbeeCourse.aiWizard.extractingFile')}
                        </div>
                    )}
                </FileUploadBox>
            </div>

            {/* Character Counter */}
            <div className="AIContextGenerator__char-counter">
                <InputCharCount
                    value={subject + promptInstructions + extractedFileText}
                    maxLength={MAX_CHARACTER_COUNT}
                    onErrorMessage={i18n.translate('weezbeeCourse.aiWizard.characterLimitExceeded')}
                />
            </div>
        </div>
    );

    // If embedded mode, return content without Modal wrapper
    if (embeddedMode) {
        return content;
    }

    // Otherwise, wrap with Modal and add footer
    return (
        <Modal open={open} onClose={onClose}>
            <div className="AIContextGeneratorModal">
                {/* Modal Title */}
                <div className="AIContextGeneratorModal__header">
                    <h2 className="AIContextGeneratorModal__title">
                        {getAIGenerationTitle({ type: signDocType })}
                    </h2>
                </div>

                {/* Modal Content */}
                <div className="AIContextGeneratorModal__content">
                    {content}
                </div>

                {/* Footer with action buttons - only in standalone mode */}
                <div className="AIContextGeneratorModal__footer">
                    <Button
                        text={i18n.translate('general.cancel')}
                        onClick={onClose}
                        color={buttonColors.GREY}
                        variant="outlined"
                        dataTestId="context-cancel-button"
                    />

                    <Button
                        text={i18n.translate('general.create')}
                        onClick={() => {
                            onSave();
                        }}
                        disabled={!isFormValid}
                        color={buttonColors.YELLOW}
                        variant="contained"
                        dataTestId="context-continue-button"
                    />
                </div>
            </div>
        </Modal>
    );
};
